import React from "react";
import { useQuery } from "@tanstack/react-query";
import { getScenarioComparison } from "@/lib/api";
import { SCENARIO_CAPTIONS } from "@/store/appStore";
import { statusMeta, STREAM_COLORS } from "@/lib/format";
import { SectionCard, StatusChip } from "@/components/tmo/primitives";
import { RingGauge } from "@/components/tmo/GaugeRadial";
import { Skeleton } from "@/components/ui/skeleton";
import { CloudSun, Cloud, CloudRain, CloudLightning, TrendingDown, TrendingUp, Minus, Info } from "lucide-react";

const SCENARIO_ICON = { BLUE_SKY: CloudSun, GRAY_SKY: Cloud, BLACK_SKY: CloudRain, BEYOND_BLACK_SKY: CloudLightning };

function ScoreCell({ score, status, sub }) {
  const m = statusMeta(status);
  return (
    <td className="py-3 px-3 text-center align-middle">
      <div className="inline-flex flex-col items-center gap-1">
        <span className="tnum font-bold text-base" style={{ color: m.color }}>{score == null ? "N/A" : `${score}%`}</span>
        <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-full" style={{ backgroundColor: m.bg, color: m.text }}>{m.label}</span>
        {sub && <span className="text-[10px] text-cnp-textMuted">{sub}</span>}
      </div>
    </td>
  );
}

export default function ScenarioComparison() {
  const { data, isLoading } = useQuery({ queryKey: ["scenario-comparison"], queryFn: getScenarioComparison });

  if (isLoading || !data) return <div className="grid grid-cols-4 gap-4">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-64 rounded-lg" />)}</div>;

  const { scenarios, overall_delta, target } = data;
  const allSame = scenarios.every((s) => s.overall.score === scenarios[0].overall.score);
  const DeltaIcon = overall_delta < 0 ? TrendingDown : overall_delta > 0 ? TrendingUp : Minus;
  const deltaColor = overall_delta < 0 ? "#D0021B" : overall_delta > 0 ? "#00A651" : "#6B7280";

  return (
    <div className="space-y-4 pb-16" data-testid="scenario-comparison-page">
      {/* Scenario summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {scenarios.map((s) => {
          const Icon = SCENARIO_ICON[s.key] || CloudSun;
          const m = statusMeta(s.overall.status);
          return (
            <SectionCard key={s.key} title={s.label} testid={`scenario-card-${s.key}`}
              action={<Icon size={18} className="text-cnp-textMuted" />}>
              <div className="flex flex-col items-center">
                <RingGauge value={s.overall.score} status={s.overall.status} size={140} label="READY" />
                <span className="mt-2 text-[11px] text-cnp-textMuted">{SCENARIO_CAPTIONS[s.key]}</span>
                <div className="mt-3 w-full flex items-center justify-between text-xs">
                  <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full" style={{ background: "#00A651" }} />{s.distribution.ready}</span>
                  <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full" style={{ background: "#F5A623" }} />{s.distribution.at_risk}</span>
                  <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full" style={{ background: "#D0021B" }} />{s.distribution.not_ready}</span>
                  <span className="text-cnp-textMuted">of {s.distribution.total}</span>
                </div>
              </div>
            </SectionCard>
          );
        })}
      </div>

      {/* Delta banner */}
      <div className="rounded-lg border border-cnp-border bg-white px-5 py-3 flex items-center gap-3" data-testid="scenario-delta-banner">
        <DeltaIcon size={20} style={{ color: deltaColor }} />
        <span className="text-sm text-cnp-text">
          Overall readiness shift from <b>Blue Sky</b> to <b>Beyond Black Sky</b>:
          <b className="tnum ml-1" style={{ color: deltaColor }}>{overall_delta > 0 ? "+" : ""}{overall_delta} pts</b>
        </span>
        <span className="ml-auto text-xs text-cnp-textMuted">Target {target}%</span>
      </div>

      {allSame && (
        <div className="rounded-lg border border-cnp-blue/30 bg-cnp-blue/5 px-5 py-3 flex items-start gap-2.5 text-xs text-cnp-text" data-testid="scenario-identical-note">
          <Info size={15} className="text-cnp-blue shrink-0 mt-0.5" />
          <span>Scores are identical across scenarios because the current test evidence is the same for every scenario (each application's test is inherited across Blue&nbsp;Sky → Beyond&nbsp;Black&nbsp;Sky). This view will differentiate once scenario-specific test outcomes are captured in the tracker.</span>
        </div>
      )}

      {/* Comparison matrix */}
      <SectionCard title="Readiness By Stream & Scenario" testid="scenario-matrix">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b-2 border-cnp-border">
                <th className="text-left py-2 px-3 font-semibold text-cnp-textMuted">Stream</th>
                {scenarios.map((s) => (
                  <th key={s.key} className="py-2 px-3 font-semibold text-cnp-text text-center">{s.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-cnp-border bg-cnp-bg/60">
                <td className="py-3 px-3 font-bold text-cnp-navy">Overall</td>
                {scenarios.map((s) => <ScoreCell key={s.key} score={s.overall.score} status={s.overall.status} />)}
              </tr>
              {["SUBSCRIBER", "TECHNICIAN", "EXECUTIVE", "SHARED"].map((streamKey) => {
                const first = scenarios[0].streams.find((x) => x.stream === streamKey);
                return (
                  <tr key={streamKey} className="border-b border-cnp-border/60">
                    <td className="py-3 px-3 font-medium">
                      <span className="flex items-center gap-2">
                        <span className="h-2.5 w-2.5 rounded-full" style={{ background: STREAM_COLORS[streamKey] }} />
                        {first?.label || streamKey}
                      </span>
                    </td>
                    {scenarios.map((s) => {
                      const st = s.streams.find((x) => x.stream === streamKey);
                      return <ScoreCell key={s.key} score={st?.score} status={st?.status} sub={`${st?.ready}/${st?.total} ready`} />;
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </SectionCard>

      {/* Driver comparison */}
      <SectionCard title="TRI Drivers By Scenario" testid="scenario-drivers">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b-2 border-cnp-border">
                <th className="text-left py-2 px-3 font-semibold text-cnp-textMuted">Driver</th>
                {scenarios.map((s) => <th key={s.key} className="py-2 px-3 font-semibold text-cnp-text text-center">{s.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {[["Test Recency", "40%"], ["Test Depth", "30%"], ["Scenario Coverage", "15%"], ["Test Success Rate", "15%"]].map(([label, wt]) => (
                <tr key={label} className="border-b border-cnp-border/60">
                  <td className="py-3 px-3 font-medium">{label} <span className="ml-1 text-[10px] font-semibold text-cnp-blue bg-cnp-blue/10 rounded px-1 py-0.5">{wt} of TRI</span></td>
                  {scenarios.map((s) => {
                    const v = s.drivers[label];
                    const col = v >= 90 ? "#00733A" : v >= 70 ? "#8A5A00" : "#9A0114";
                    return <td key={s.key} className="py-3 px-3 text-center tnum font-semibold" style={{ color: col }}>{v}%</td>;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SectionCard>
    </div>
  );
}
